import { useMemo } from 'react'
import { orderBy } from 'lodash'
import BigNumber from 'bignumber.js'
import { useAccount } from 'wagmi'
import { useTokenBalances } from '~/state/wallet/hooks'
import { useAllLimitSwapTokens } from '~/state/dex/hooks'
import { useTokensPrice } from '~/hooks/useTokenPrice'
import { OrderedLimitSwapToken } from './useOrderedTokenList'
import { getAddress } from 'viem'
import { FixedPointNumber } from '~/utils/fixedPoint'

export const useAllSortedLimitTokens = () => {
  const { address: account, chainId: currentChainId } = useAccount()

  const allTokens = useAllLimitSwapTokens()
  const currentNetworkTokens = allTokens.filter(
    (token) => token.chainId === currentChainId,
  )
  const currentTokensBalances = useTokenBalances(currentNetworkTokens)
  const currentTokensPrices = useTokensPrice(
    currentNetworkTokens.map((token) => token.address),
    'USD',
    currentChainId,
  )

  return useMemo((): OrderedLimitSwapToken[] => {
    const validTokens = allTokens.filter((token) => token.symbol != null)
    const initSortedTokens = orderBy(
      validTokens,
      ['chainId', (item) => item.symbol.toLowerCase()],
      ['asc', 'asc'],
    )

    if (account) {
      const resetTokens = initSortedTokens.filter(
        (token) => token.chainId !== currentChainId,
      )

      const filledCurrentNetworkTokens = currentNetworkTokens.map((token) => {
        const address = getAddress(token.address)
        const priceUSD = new BigNumber(currentTokensPrices[address] || 0)
          .multipliedBy(
            currentTokensBalances[token.address]?.toDecimalsFixed() || 0,
          )
          .toFixed(2)
        const balance =
          currentTokensBalances[token.address] || new FixedPointNumber(0)
        const price = currentTokensPrices[address]

        return {
          ...token,
          balance,
          price,
          priceUSD: isNaN(Number(priceUSD)) ? '0' : priceUSD,
        }
      }) as OrderedLimitSwapToken[]
      const sortedCurrentNetworkTokens = orderBy(
        filledCurrentNetworkTokens,
        [
          (item) => Number(item.priceUSD),
          (item) => Number(item.balance),
          'symbol',
        ],
        ['desc', 'desc', 'asc'],
      )
      return sortedCurrentNetworkTokens.concat(resetTokens)
    }
    return initSortedTokens
  }, [
    account,
    allTokens,
    currentChainId,
    currentNetworkTokens,
    currentTokensBalances,
    currentTokensPrices,
  ])
}
